import type { SpellDictionarySource, SpellRequest, SpellResponse } from './protocol';

/** The part of a `Worker` the client uses — a test hands in a fake. */
export interface SpellPort {
  postMessage(message: SpellRequest): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<SpellResponse>) => void): void;
  addEventListener(type: 'error', listener: (event: Event) => void): void;
}

type Pending = {
  resolve(response: SpellResponse): void;
  reject(error: Error): void;
};

/**
 * Request/response over the worker's message channel. Every request gets an
 * id; a worker that fails (script not loadable, crashed) rejects everything
 * pending and everything after, so the editor simply stops underlining.
 */
export class SpellClient {
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private failure: Error | undefined;

  constructor(private readonly port: SpellPort) {
    port.addEventListener('message', (event) => {
      const response = event.data;
      const waiting = this.pending.get(response.id);
      if (!waiting) return;
      this.pending.delete(response.id);
      waiting.resolve(response);
    });
    port.addEventListener('error', () => {
      this.failure = new Error('The spell-check worker failed.');
      for (const waiting of this.pending.values()) waiting.reject(this.failure);
      this.pending.clear();
    });
  }

  /** Whether the worker failed; a failed client stays failed. */
  get failed(): boolean {
    return !!this.failure;
  }

  /** The words of `words` the dictionary does not know. */
  async check(dictionary: SpellDictionarySource, words: readonly string[]): Promise<readonly string[]> {
    const response = await this.request((id) => ({ id, kind: 'check', dictionary, words }));
    if (response.kind === 'error') throw new Error(response.message);
    return response.kind === 'checked' ? response.incorrect : [];
  }

  /** Up to `limit` corrections for a word, best first. */
  async suggest(dictionary: SpellDictionarySource, word: string, limit = 5): Promise<readonly string[]> {
    const response = await this.request((id) => ({ id, kind: 'suggest', dictionary, word, limit }));
    if (response.kind === 'error') throw new Error(response.message);
    return response.kind === 'suggested' ? response.suggestions : [];
  }

  private request(build: (id: number) => SpellRequest): Promise<SpellResponse> {
    if (this.failure) return Promise.reject(this.failure);
    const id = this.nextId++;
    return new Promise<SpellResponse>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.port.postMessage(build(id));
    });
  }
}

let shared: SpellClient | null | undefined;

/**
 * The page's one spell-check worker, started on first use and kept for the
 * page's lifetime so a loaded dictionary stays loaded. `null` where workers are
 * unavailable (tests, SSR, a strict CSP): the editor then shows no underlines.
 */
export function sharedSpellClient(): SpellClient | null {
  if (shared === undefined) shared = startWorker();
  return shared && !shared.failed ? shared : null;
}

function startWorker(): SpellClient | null {
  if (typeof Worker === 'undefined') return null;
  try {
    const worker = new Worker(new URL('./spell.worker.ts', import.meta.url), {
      type: 'module',
      name: 'spell',
    });
    return new SpellClient(worker as unknown as SpellPort);
  } catch {
    return null;
  }
}
